import { useEffect, useState } from 'react';

/**
 * Custom hook for tracking multiple media query matches with window resize listener
 *
 * @param breakpoints - Array of breakpoint values in pixels
 * @returns Array of booleans indicating whether each breakpoint currently matches (min-width)
 *
 * @example
 * ```tsx
 * const [isMobile, isTablet, isDesktop] = useMediaQuery([320, 768, 1024]);
 * // isMobile: min-width: 320px
 * // isTablet: min-width: 768px
 * // isDesktop: min-width: 1024px
 * ```
 */
export function useMediaQuery(breakpoints: number[]): boolean[] {
  // Initialize state - handle SSR by defaulting to false for all breakpoints
  const [matches, setMatches] = useState<boolean[]>(() => {
    if (typeof window === 'undefined') {
      return breakpoints.map(() => false);
    }

    try {
      return breakpoints.map((breakpoint) => window.matchMedia(`(min-width: ${breakpoint}px)`).matches);
    } catch {
      return breakpoints.map(() => false);
    }
  });

  useEffect(() => {
    // Early return for SSR
    if (typeof window === 'undefined') {
      return;
    }

    const mediaQueryLists: MediaQueryList[] = [];
    const handlersMap = new Map<MediaQueryList, (event: MediaQueryListEvent) => void>();

    try {
      // Create media query lists for each breakpoint
      breakpoints.forEach((breakpoint, index) => {
        const mediaQueryList = window.matchMedia(`(min-width: ${breakpoint}px)`);
        mediaQueryLists.push(mediaQueryList);

        // Define the event handler for this specific breakpoint
        const handleChange = (event: MediaQueryListEvent): void => {
          setMatches((prevMatches) => {
            const newMatches = [...prevMatches];
            newMatches[index] = event.matches;
            return newMatches;
          });
        };

        handlersMap.set(mediaQueryList, handleChange);

        // Add event listener
        if (mediaQueryList.addEventListener) {
          mediaQueryList.addEventListener('change', handleChange);
        } else {
          // Fallback for older browsers
          mediaQueryList.addListener(handleChange);
        }
      });

      // Update state to current match status for all breakpoints
      setMatches(mediaQueryLists.map((mql) => mql.matches));
    } catch {
      // Invalid media queries
      return;
    }

    // Cleanup function
    return () => {
      mediaQueryLists.forEach((mediaQueryList) => {
        const handler = handlersMap.get(mediaQueryList);
        if (handler) {
          if (mediaQueryList.removeEventListener) {
            mediaQueryList.removeEventListener('change', handler);
          } else {
            // Fallback for older browsers
            mediaQueryList.removeListener(handler);
          }
        }
      });
    };
  }, [breakpoints.join(',')]);

  return matches;
}
